/* Karte teilen: Freigaben per E-Mail an andere Benutzer, lesend oder schreibend. Nur der Besitzer (und Admins) sieht diesen Dialog. */
import { useCallback, useEffect, useState } from 'react';
import type { BackendClient, MapMeta, ShareInfo } from '../net/backend.ts';
import { Dialog, DialogBody, DialogFoot } from './dialog.tsx';
import { useT } from './hooks.ts';

type Perm = ShareInfo['perm'];

export function ShareDialog(props: { client: BackendClient; map: MapMeta; onClose: () => void }) {
  const t = useT();
  const { client, map } = props;
  const [items, setItems] = useState<ShareInfo[] | null>(null);
  const [max, setMax] = useState(0);
  const [email, setEmail] = useState('');
  const [perm, setPerm] = useState<Perm>('read');
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try { const r = await client.shareList(map.id); setItems(r.items); setMax(r.max); }
    catch (e) { setMsg(t('share.failed', { msg: (e as Error).message })); }
  }, [client, map.id, t]);
  useEffect(() => { void refresh(); }, [refresh]);

  const guard = async (fn: () => Promise<void>) => {
    try { await fn(); await refresh(); }
    catch (e) {
      const st = (e as { status?: number }).status;
      setMsg(st === 404 ? t('share.no_user') : st === 429 ? t('share.limit') : st === 400 ? t('share.self') : t('share.failed', { msg: (e as Error).message }));
    }
  };
  const add = () => guard(async () => {
    const v = email.trim(); if (!v) return;
    await client.shareSet(map.id, v, perm);
    setEmail(''); setMsg(t('share.added', { email: v }));
  });
  const change = (s: ShareInfo, p: Perm) => guard(async () => { await client.shareSet(map.id, s.email, p); });
  const remove = (s: ShareInfo) => guard(async () => { await client.shareRemove(map.id, s.user); setMsg(t('share.removed', { email: s.email })); });

  return (
    <Dialog title={t('share.title', { name: map.name })} onClose={props.onClose}>
      <DialogBody>
        <p className="small">{t('share.intro')}</p>
        {items && !items.length && <p className="small">{t('share.none')}</p>}
        <div className="belist">
          {items?.map((s) => (
            <div key={s.user} className="row berow">
              <span className="bepath">{s.email}</span>
              <select className="sel" aria-label={t('share.perm_of', { email: s.email })} value={s.perm} onChange={(e) => void change(s, e.target.value as Perm)}>
                <option value="read">{t('share.read')}</option>
                <option value="write">{t('share.write')}</option>
              </select>
              <button className="btn" onClick={() => void remove(s)}>{t('share.remove')}</button>
            </div>
          ))}
        </div>
        {items && max > 0 && <p className="small">{t('share.count', { n: items.length, max })}</p>}
        <div className="row">
          <input type="email" className="inp" aria-label={t('share.email')} placeholder={t('share.email')} value={email}
            onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
          <select className="sel" aria-label={t('share.perm')} value={perm} onChange={(e) => setPerm(e.target.value as Perm)}>
            <option value="read">{t('share.read')}</option>
            <option value="write">{t('share.write')}</option>
          </select>
          <button className="btn primary" onClick={() => void add()}>{t('share.add')}</button>
        </div>
        {msg && <p className="small" role="status">{msg}</p>}
      </DialogBody>
      <DialogFoot><button className="btn" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}
